/**
 * MetadataPage コンポーネント
 *
 * JSON-LD / RDF データセットの配布情報とモデリング方針を説明するページ。
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/organisms/Header';
import { Footer } from '@/components/organisms/Footer';
import { useI18n } from '@/i18n/useI18n';
import {
  METADATA_DISTRIBUTION_LINKS,
  METADATA_LAST_UPDATED,
  toMetadataPublicUrl,
} from '@/lib/metadata-links';

const PREFIXES = [
  { id: 'schema', iri: 'https://schema.org/' },
  { id: 'dcterms', iri: 'http://purl.org/dc/terms/' },
  { id: 'skos', iri: 'http://www.w3.org/2004/02/skos/core#' },
  { id: 'rdf', iri: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#' },
  { id: 'rdfs', iri: 'http://www.w3.org/2000/01/rdf-schema#' },
] as const;

const CONTROLLED_SCHEMES = [
  { id: 'genre', uri: 'https://takun-archive.org/vocab/genre' },
  {
    id: 'presentationForm',
    uri: 'https://takun-archive.org/vocab/presentation_form',
  },
  { id: 'eventDetail', uri: 'https://takun-archive.org/vocab/event_detail' },
] as const;

const WORK_JSONLD_SAMPLE = `{
  "@context": {
    "schema": "https://schema.org/",
    "dcterms": "http://purl.org/dc/terms/"
  },
  "@id": "https://takun-archive.org/work/000001",
  "@type": "schema:Movie",
  "schema:name": { "ja": "ZURAW", "en": "ZURAW" },
  "schema:dateCreated": "1964",
  "schema:genre": { "@id": "https://takun-archive.org/vocab/genre/animation" },
  "schema:subjectOf": [{ "@id": "https://takun-archive.org/event/100002" }],
  "dcterms:created": "2026-01-01T00:00:00.000Z",
  "dcterms:modified": "2026-02-25T21:39:25.173Z"
}`;

const EVENT_JSONLD_SAMPLE = `{
  "@context": {
    "schema": "https://schema.org/",
    "dcterms": "http://purl.org/dc/terms/"
  },
  "@id": "https://takun-archive.org/event/100002",
  "@type": "schema:ScreeningEvent",
  "schema:name": { "ja": "草月シネマテーク第11回 アニメーション・フェスティバル（第1回）" },
  "schema:startDate": "1964-09-21",
  "schema:endDate": "1964-09-26",
  "schema:workFeatured": [{ "@id": "https://takun-archive.org/work/000001" }],
  "dcterms:created": "2026-01-01T00:00:00.000Z",
  "dcterms:modified": "2026-02-25T21:39:25.174Z"
}`;

/**
 * MetadataPage コンポーネント
 */
export function MetadataPage() {
  const { t } = useI18n();

  const datasetJsonLd = useMemo(() => {
    const distribution = METADATA_DISTRIBUTION_LINKS.map((file) => ({
      '@type': 'DataDownload',
      name: t(`metadataPage.sections.distribution.items.${file.id}.name`),
      encodingFormat: file.format,
      contentUrl: toMetadataPublicUrl(file.samplePath ?? file.path),
    }));

    return {
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: t('metadataPage.datasetJsonLd.name'),
      description: t('metadataPage.datasetJsonLd.description'),
      url: toMetadataPublicUrl('/metadata'),
      inLanguage: ['ja', 'en'],
      isAccessibleForFree: true,
      license: 'https://creativecommons.org/licenses/by/4.0/',
      dateModified: METADATA_LAST_UPDATED,
      creator: {
        '@type': 'Organization',
        name: 'Takun Archive',
      },
      distribution,
    };
  }, [t]);

  return (
    <div className="flex min-h-screen flex-col">
      <Helmet>
        <title>
          {t('metadataPage.title')} | {t('site.title')}
        </title>
        <meta name="description" content={t('metadataPage.metaDescription')} />
        <script type="application/ld+json">
          {JSON.stringify(datasetJsonLd)}
        </script>
      </Helmet>

      <Header />

      <main id="main-content" className="flex-1">
        <section className="border-b bg-gradient-to-b from-background to-muted/20 py-12">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              {t('metadataPage.title')}
            </h1>
            <p className="mt-4 max-w-3xl text-muted-foreground">
              {t('metadataPage.intro')}
            </p>
          </div>
        </section>

        <section className="py-10">
          <div className="container mx-auto max-w-5xl space-y-10 px-4">
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight">
                {t('metadataPage.sections.overview.title')}
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t('metadataPage.sections.overview.purpose')}</p>
                <p>{t('metadataPage.sections.overview.policy')}</p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight">
                {t('metadataPage.sections.distribution.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('metadataPage.sections.distribution.intro')}
              </p>

              <div className="overflow-x-auto rounded-lg border">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">
                        {t('metadataPage.sections.distribution.table.name')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        {t('metadataPage.sections.distribution.table.path')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        {t('metadataPage.sections.distribution.table.note')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {METADATA_DISTRIBUTION_LINKS.map((item) => (
                      <tr key={item.id} className="border-t align-top">
                        <td className="px-4 py-3 font-medium text-foreground">
                          {t(
                            `metadataPage.sections.distribution.items.${item.id}.name`
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs">{item.path}</code>
                          <div className="mt-1">
                            <a
                              href={item.samplePath ?? item.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline"
                            >
                              {t('metadataPage.sections.distribution.example', {
                                path: item.samplePath ?? item.path,
                              })}
                            </a>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {t(
                            `metadataPage.sections.distribution.items.${item.id}.note`
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                <li>{t('metadataPage.sections.distribution.notes.format')}</li>
                <li>
                  {t('metadataPage.sections.distribution.notes.entityAccess')}
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight">
                {t('metadataPage.sections.model.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('metadataPage.sections.model.intro')}
              </p>

              <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-start">
                <article className="rounded-lg border p-4">
                  <h3 className="text-lg font-semibold">
                    {t('metadataPage.sections.model.work.title')}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('metadataPage.sections.model.work.type')}
                  </p>
                  <h4 className="mt-4 text-sm font-semibold">
                    {t('metadataPage.sections.model.work.fieldsTitle')}
                  </h4>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <li>{t('metadataPage.sections.model.work.fields.id')}</li>
                    <li>{t('metadataPage.sections.model.work.fields.type')}</li>
                    <li>{t('metadataPage.sections.model.work.fields.name')}</li>
                    <li>
                      {t('metadataPage.sections.model.work.fields.dateCreated')}
                    </li>
                    <li>
                      {t('metadataPage.sections.model.work.fields.genre')}
                    </li>
                    <li>
                      {t('metadataPage.sections.model.work.fields.subjectOf')}
                    </li>
                  </ul>
                </article>

                <article className="rounded-lg border bg-muted/20 p-4">
                  <h3 className="text-sm font-semibold">
                    {t('metadataPage.sections.model.relations.title')}
                  </h3>
                  <div className="mt-3 space-y-3 text-sm">
                    <div>
                      <p className="font-medium">
                        {t(
                          'metadataPage.sections.model.relations.workToEventLabel'
                        )}
                      </p>
                      <p className="font-mono text-xs text-muted-foreground">
                        {t(
                          'metadataPage.sections.model.relations.workToEventValue'
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">
                        {t(
                          'metadataPage.sections.model.relations.eventToWorkLabel'
                        )}
                      </p>
                      <p className="font-mono text-xs text-muted-foreground">
                        {t(
                          'metadataPage.sections.model.relations.eventToWorkValue'
                        )}
                      </p>
                    </div>
                  </div>
                </article>

                <article className="rounded-lg border p-4">
                  <h3 className="text-lg font-semibold">
                    {t('metadataPage.sections.model.event.title')}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('metadataPage.sections.model.event.type')}
                  </p>
                  <h4 className="mt-4 text-sm font-semibold">
                    {t('metadataPage.sections.model.event.fieldsTitle')}
                  </h4>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <li>{t('metadataPage.sections.model.event.fields.id')}</li>
                    <li>
                      {t('metadataPage.sections.model.event.fields.type')}
                    </li>
                    <li>
                      {t('metadataPage.sections.model.event.fields.name')}
                    </li>
                    <li>
                      {t('metadataPage.sections.model.event.fields.startDate')}
                    </li>
                    <li>
                      {t('metadataPage.sections.model.event.fields.location')}
                    </li>
                    <li>
                      {t(
                        'metadataPage.sections.model.event.fields.workFeatured'
                      )}
                    </li>
                  </ul>
                </article>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight">
                {t('metadataPage.sections.vocabulary.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('metadataPage.sections.vocabulary.intro')}
              </p>

              <div className="overflow-x-auto rounded-lg border">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">
                        {t(
                          'metadataPage.sections.vocabulary.prefixTable.prefix'
                        )}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        {t('metadataPage.sections.vocabulary.prefixTable.iri')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        {t(
                          'metadataPage.sections.vocabulary.prefixTable.usage'
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {PREFIXES.map((prefix) => (
                      <tr key={prefix.id} className="border-t align-top">
                        <td className="px-4 py-3 font-mono text-xs">
                          {prefix.id}
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs">{prefix.iri}</code>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {t(
                            `metadataPage.sections.vocabulary.prefixUsage.${prefix.id}`
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-semibold">
                {t('metadataPage.sections.vocabulary.schemesTitle')}
              </h3>
              <div className="grid gap-3 md:grid-cols-3">
                {CONTROLLED_SCHEMES.map((scheme) => (
                  <article key={scheme.id} className="rounded-lg border p-4">
                    <h4 className="font-semibold">
                      {t(
                        `metadataPage.sections.vocabulary.schemes.${scheme.id}.name`
                      )}
                    </h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t(
                        `metadataPage.sections.vocabulary.schemes.${scheme.id}.description`
                      )}
                    </p>
                    <code className="mt-3 block text-xs">{scheme.uri}</code>
                  </article>
                ))}
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight">
                {t('metadataPage.sections.samples.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('metadataPage.sections.samples.intro')}
              </p>
              <div className="space-y-4">
                <article className="rounded-lg border">
                  <h3 className="border-b bg-muted/40 px-4 py-2 text-sm font-semibold">
                    {t('metadataPage.sections.samples.workTitle')}
                  </h3>
                  <pre className="overflow-x-auto p-4 text-xs leading-relaxed">
                    <code>{WORK_JSONLD_SAMPLE}</code>
                  </pre>
                </article>
                <article className="rounded-lg border">
                  <h3 className="border-b bg-muted/40 px-4 py-2 text-sm font-semibold">
                    {t('metadataPage.sections.samples.eventTitle')}
                  </h3>
                  <pre className="overflow-x-auto p-4 text-xs leading-relaxed">
                    <code>{EVENT_JSONLD_SAMPLE}</code>
                  </pre>
                </article>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight">
                {t('metadataPage.sections.license.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('metadataPage.sections.license.description')}
              </p>
              <Link
                to="/about"
                className="inline-block text-sm text-primary hover:underline"
              >
                {t('metadataPage.sections.license.aboutLinkLabel')}
              </Link>
            </section>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
